import Decimal from 'decimal.js-light';
import { useEffect, useRef } from 'react';

import { tokenFormatter } from '@/lib/formatting';

import type { ChainAddress } from '@/utils/models';

import { useTradeModuleStore, type TradeModuleStore } from './store';
import { useTradeEngine } from './useTradeEngine';

type UseSyncInputUsdArgs = {
  sellTokenAddress: ChainAddress | undefined;
  sellTokenChainId: number | undefined;
  buyTokenAddress: ChainAddress | undefined;
  buyTokenChainId: number | undefined;
};

const inputUsdSyncSelector = (state: TradeModuleStore) => ({
  buyInput: state.buyInput,
  sellInput: state.sellInput,
  inputMode: state.inputMode,
  updateBuyInput: state.updateBuyInput,
  updateSellInput: state.updateSellInput,
});

/** Keeps rewrite token inputs synchronized with fresh engine USD prices. */
export function useSyncInputUsd({
  sellTokenAddress,
  sellTokenChainId,
  buyTokenAddress,
  buyTokenChainId,
}: UseSyncInputUsdArgs): void {
  const { tokenUsdPrices, tokenUsdPriceAvailability } = useTradeEngine();
  const { buyInput, sellInput, inputMode, updateBuyInput, updateSellInput } =
    useTradeModuleStore(inputUsdSyncSelector);

  const updateSwapInput = (input: 'buy' | 'sell', priceUsd: number) => {
    const swapInput = input === 'buy' ? buyInput : sellInput;
    const { decimal, usd } = swapInput;

    if (!decimal && usd === 0) return;

    const updateInput = input === 'buy' ? updateBuyInput : updateSellInput;

    if (inputMode === 'tokenAmount') {
      const newAmountUsd = decimal ? new Decimal(`0${decimal}`).mul(priceUsd).toNumber() : 0;
      updateInput({ ...swapInput, usd: newAmountUsd });
    } else if (Number.isFinite(priceUsd) && priceUsd > 0) {
      const newAmount = new Decimal(usd).div(priceUsd).toString();
      const newFormatted = tokenFormatter.format(newAmount, { mode: 'write' });
      updateInput({ usd, decimal: newAmount, formatted: newFormatted });
    } else if (priceUsd === 0) {
      updateInput({ usd, decimal: '', formatted: '' });
    }
  };

  // Latest-ref: the pair-change effect reads fresh inputs without re-running on them.
  const updateSwapInputRef = useRef(updateSwapInput);
  updateSwapInputRef.current = updateSwapInput;

  useEffect(() => {
    const syncInput = updateSwapInputRef.current;
    if (tokenUsdPriceAvailability.sellToken) {
      syncInput('sell', tokenUsdPrices.sellTokenUsd);
    }
    if (tokenUsdPriceAvailability.buyToken) {
      syncInput('buy', tokenUsdPrices.buyTokenUsd);
    }
  }, [
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    tokenUsdPriceAvailability.buyToken,
    tokenUsdPriceAvailability.sellToken,
    tokenUsdPrices.buyTokenUsd,
    tokenUsdPrices.sellTokenUsd,
  ]);
}
